// src/App.jsx
import React, {useEffect, useRef, useState, useCallback, useMemo, Suspense} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Lazy-loaded graph components
const OCDFGViewer = React.lazy(() => import('./features/graph/OCDFGViewer'));
const ReactFlowGraph = React.lazy(() => import('./features/graph/ReactFlowGraph'));
// Backwards compatibility alias
const ReactFlowViewer = ReactFlowGraph;

// KPI Domain imports
import {
  CountsTiles,
  ActivityFrequencyRatioKPI,
  ActivityReoccurrenceKPI,
  DateDeviationKPI,
  LiefertermintreueKPI,
  LeadTimeDashboard,
  // Renamed components (with backwards compatibility)
  EdgeDuration,
  EdgeFrequency,
  ObjectCountsBySelectedActivities,
  LeadTimeStatistics,
  LifecycleTimeKPI,
  // Backwards compatibility aliases
  EdgeTimeKPI,
  EdgeEventCountCard,
  ObjectCountByActivityKPI,
  AttributeFilteredStats,
  AggregationKPI,
  AutomationPotentialKPI,
  OverdueValueTimeline,
  LiefertermintreueTimeline,
  AbsoluteDelayTimeline,
  DurchlaufzeitTimeline,
  EfficiencyKPI,
  ObjectCountKPI,
  ObjectConversionKPI,
} from './features/kpi';

// Visualization Domain imports
import {
  LeadTimeHistogram,
  AttributeTable,
  ObjectTable,
  EdgeDurationHistogram,
  TimeSpanTile,
  TracePivotTable,
  GanttChart,
  // Renamed components (with backwards compatibility)
  TimelineDiagram,
  WeekdayActivityCount,
  // Backwards compatibility aliases
  TimelineMetrics,
  WeeklyActivityPatternChart,
  BiaxialScatterPlot,
} from './features/visualization';
import ActivityFrequencyChart from './features/visualization/ActivityFrequencyChart';
import TotalDowntimeKPI from './features/kpi/TotalDowntimeKPI';
import ResourceUtilizationKPI from './features/kpi/ResourceUtilizationKPI';
import ResourceUtilizationChart from './features/kpi/ResourceUtilizationChart';
import AttributeSumBarChart from './features/kpi/AttributeSumBarChart';
import ResourceCalendar from './features/kpi/resourceCalendar';
import AttributeSumLineChart from './features/kpi/AttributeSumLineChart';
import TransitionTimeLineChart from './features/kpi/TransitionTimeLineChart';
import ResourceStatusMonitor from './features/kpi/ResourceStatusMonitor';
import ExecutiveResourceUtilizationTile from './features/kpi/ExecutiveResourceUtilizationTile';

// Variants Domain imports
import {
  VariantExplorer,
  VariantExplorerExtended,
} from './features/variants';

// ABC/XYZ Analysis Domain imports
import { ABCXYZAnalysisPanel } from './features/abc-xyz';
// Conformance Domain imports
import { VisualConformanceChecker } from './features/conformance';

// Root Cause Domain imports
import { RootCauseWizard } from './features/rootcause';

// OCEL Domain imports
import {
  ActivityCountsTile,
  EventCountsPerObjectTypeTile,
  EventTypesPerObjectTypeTile,
  EventIntensityPerObjectTypeTile,
  AvgEventsPerObjectType,
  ObjectTypeFrequenciesTile,
  ObjectDetails,
  // Moved from KPI domain (with backwards compatibility)
  MissingTimestamps,
  MissingActivities,
  MissingObjectTypes,
  MissingEventObjectRef,
  ObjectTypeCombinations,
  OrphanObjects,
  // Backwards compatibility aliases
  MissingTimestampsTile,
  MissingActivityTile,
  MissingObjectTypeTile,
  MissingEventObjectRefsTile,
  ObjectTypeCombinationsTile,
  OrphanObjectsTile,
} from './features/ocel';

// File Manager Domain imports
import { OCELFileManager } from './features/file-manager';

// Cache Domain imports
import { CacheSettings } from './shared';


// Misc Domain imports
import {
  PreActivitySelector,
  ComponentLibrary,
  // Backwards compatibility aliases
  ActivitySequenceBuilder,
  ComponentSelector,
} from './features/misc';

// Services
import { getActiveFileType, getAvailableFiles } from './services/apiClient';
import { getStoredActiveFile, setStoredActiveFile } from './services/activeFileStorage';
import { getUserDashboardState } from './services/userDashboardState';
import { setStoredActiveSharedConfigId } from './services/activeSharedConfigStorage';
import { useDashboardConfig } from './hooks/useDashboardConfig';
import { useAuth } from './stores/AuthContext';

// Styles
import './styles/globals.css'
import './styles/grid-layout.css'

// Note: Store providers are now handled by AppProviders component

// Note: Activity Frequency features removed in this branch

import AppSidebar from './components/layout/AppSidebar';
import AppHeader from './components/layout/AppHeader';
import ToolbarButtons from './components/layout/ToolbarButtons';
import ConfigManagerModal from './components/layout/ConfigManagerModal';
import FileSwitchLoadingOverlay from './components/layout/FileSwitchLoadingOverlay';
import HideInWorkshop from './components/common/HideInWorkshop';
import { createTab } from './components/layout/TabNavigation';
import ContentSection from './components/layout/ContentSection';

// Grid layout components
import GridLayout from './components/layout/GridLayout';
import GridComponent from './components/layout/GridComponent';
import RefreshButton from './components/Tile/RefreshButton';
import LazyTileMount from './components/Tile/LazyTileMount';

// Note: Graph types are now handled by the file manager - no need for separate buttons

// App configuration imports
import { DEFAULT_TABS, DEFAULT_COMPONENTS, SPECIAL_TABS, COMPONENT_ID_ALIASES, NON_GRID_COMPONENTS } from './app/config';
import AppProviders from './app/providers/AppProviders';
import { useWorkshopMode } from './stores/WorkshopModeContext';
import { useOcelMetadata } from './stores/OcelContext';
import { useGlobalFilters } from './stores/GlobalFilterContext/GlobalFilterContext';
import { TabFilterProvider } from './stores/GlobalFilterContext/TabFilterOverrideContext';

// Clears global filters when the active file changes (must live inside AppProviders)
const FileSwitchFilterClearer = ({ activeFile }) => {
  const { dispatch } = useGlobalFilters();
  const prevActiveFileRef = useRef(null);
  useEffect(() => {
    if (prevActiveFileRef.current !== null && prevActiveFileRef.current !== activeFile) {
      dispatch({ type: 'CLEAR_FILTERS' });
    }
    prevActiveFileRef.current = activeFile;
  }, [activeFile, dispatch]);
  return null;
};

// --- Tenant tile allowlist (feature-tiering, not a security boundary — the
// real data-access boundary stays enforce_file_type_access on the backend) ---
//
// Three-state, not two: `allowedComponentIds` is `undefined` ("not yet
// known"), `null` (confirmed unrestricted), or a `Set` (confirmed restricted
// to these alias-normalized ids). Every read site below compares with `==
// null`, which is true for BOTH `undefined` and `null` in JS — so "not yet
// known" and "confirmed unrestricted" render identically (tiles show
// normally), and ONLY a resolved `Set` ever hides anything. This is a
// deliberate choice, not an oversight: the alternative (fail-closed on
// "unknown", i.e. treating it as "nothing allowed") was tried in a prior fix
// round and reverted — it required synchronously pre-fetching metadata inside
// fetchActiveFile before the dashboard could render, which (a) fired a second,
// un-coalesced GET /ocel/metadata on every page load (OCELFileManager already
// makes this exact call on its own mount) and (b) inserted a new network
// round-trip into the isInitializing critical path, delaying first paint for
// every tenant including today's unrestricted paying customer. The brief's
// hard requirement ("timing must not change") rules that approach out.
//
// Net effect: a disallowed tile from an old saved config can flash for the
// same brief window that ANY metadata-dependent UI in this app already has
// before OcelContext's metadata resolves (bounded by OCELFileManager's own
// mount-time fetch — typically one or two renders, not indefinite) — it is
// never PERMANENTLY unenforced, which was the actual structural bug the prior
// fix round closed (App()'s own useOcelMetadata call could never see live
// context data at all — see AllowedComponentIdsResolver below). Once that
// fetch resolves, the resolver reports the real value and the render-time
// filter chains hide the disallowed tile from then on, including across a
// later file switch.
const filterComponentsByAllowlist = (components, allowedComponentIds) =>
  allowedComponentIds == null
    ? components
    : components.filter((c) => allowedComponentIds.has(COMPONENT_ID_ALIASES[c.id] || c.id));

// Resolves the live tenant allowlist from OcelContext metadata and reports it
// up to App via `onResolve`. Must live INSIDE <AppProviders> — App.jsx renders
// AppProviders as an internal child of its own JSX (see AppProviders.jsx's
// note), so App's own hooks structurally cannot call useOcelMetadata directly;
// this mirrors the existing FileSwitchFilterClearer pattern for the same
// reason. Reads metadata that OCELFileManager already fetches on mount/file
// switch — no fetch of its own, so it adds zero network requests. No-ops while
// unresolved (`metadata == null`), so a later file switch's transient gap
// before the new file's metadata lands is never misread as "confirmed
// unrestricted" and never lets a previously-hidden disallowed tile reappear.
const AllowedComponentIdsResolver = ({ activeFile, onResolve }) => {
  const metadata = useOcelMetadata(activeFile);
  useEffect(() => {
    if (metadata == null) return; // not yet resolved for this activeFile — leave the last known value alone
    const raw = metadata.allowed_component_ids;
    onResolve(Array.isArray(raw) ? new Set(raw.map((id) => COMPONENT_ID_ALIASES[id] || id)) : null);
  }, [metadata, onResolve]);
  return null;
};

// Default layout dimensions for dynamically added components (instanceId-based).
// Used when the component isn't found in the saved/pre-defined layouts.
const DEFAULT_COMPONENT_LAYOUTS = {
  'lead-time-statistics': { w: 6, h: 4, minW: 2, minH: 2, maxH: 12 },
  'lead-time-histogram':  { w: 6, h: 7, minW: 4, minH: 4, maxH: 12 },
  'gantt-chart':          { w: 12, h: 12, minW: 6, minH: 8 },
  'abc-xyz-analysis':     { w: 6, h: 10, minW: 5, minH: 6 },
  'aggregation-kpi':         { w: 4, h: 4, minW: 2, minH: 3 },
  'efficiency-kpi':          { minW: 2, minH: 2 },
  'object-count-kpi':        { minW: 2, minH: 1 },
  'object-conversion-kpi':   { minW: 2, minH: 2 },
  'liefertermintreue-kpi':   { w: 4, h: 5, minW: 2, minH: 4 },
  'biaxial-scatter-plot':    { w: 8, h: 8, minW: 4, minH: 5, maxH: 16 },
  'conformance-checker':           { w: 8,  h: 14, minW: 5, minH: 8 },
  'temporal-conformance-checker':  { w: 8,  h: 16, minW: 5, minH: 8 },
  'visual-conformance-checker':    { w: 12, h: 15, minW: 8, minH: 7 },
};

// Single unified grid layout
const UNIFIED_GRID_LAYOUTS = {
  lg: [
    // Home tab components - attr-stats top left, histogram below, date deviation KPIs to the right in 2 columns
    { i: 'attr-stats', x: 0, y: 0, w: 6, h: 2, minH: 2, maxH: 12 },
    { i: 'histogram', x: 0, y: 3, w: 6, h: 7 },
    { i: 'date-deviation-kpi', x: 6, y: 0, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-2', x: 9, y: 0, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-3', x: 6, y: 5, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-4', x: 9, y: 5, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'edge-duration', x: 0, y: 10, w: 6, h: 6, minH: 3, maxH: 12 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 16, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'prozent-card', x: 3, y: 10, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'edge-card', x: 6, y: 10, w: 3, h: 5, minH: 3, maxH: 12 },
    { i: 'weekly-pattern', x: 0, y: 15, w: 6, h: 6 },
    { i: 'timeline-metrics', x: 6, y: 15, w: 6, h: 6 },
    { i: 'global-filter-demo', x: 9, y: 10, w: 3, h: 5 },
    { i: 'cache-settings', x: 0, y: 21, w: 4, h: 6 },
    { i: 'avg-events-per-object-type', x: 4, y: 21, w: 4, h: 6 },
    { i: 'object-details', x: 8, y: 21, w: 4, h: 6 },
    { i: 'edge-time-kpi', x: 0, y: 27, w: 6, h: 6, minH: 3, maxH: 12 },
    { i: 'lifecycle-time-kpi', x: 6, y: 27, w: 6, h: 6, minH: 3, maxH: 12 },
    // activity-frequency-chart removed in this local branch
    { i: 'trace-pivot-table', x: 0, y: 57, w: 12, h: 10, minW: 4, minH: 6 },

    // Conformance
    { i: 'visual-conformance-checker', x: 0, y: 0, w: 12, h: 15, minW: 8, minH: 7 },

    // ABC/XYZ Matrix
    { i: 'abc-xyz-analysis', x: 0, y: 0, w: 6, h: 10, minW: 5, minH: 6 },

    // Test tab components - single full-width component for isolated testing
    { i: 'trace-pivot-table', x: 0, y: 0, w: 12, h: 10, minW: 4, minH: 6 },
    { i: 'activity-counts', x: 0, y: 0, w: 12, h: 8 },

    // activity-frequency-ratio-kpi remains available in test tab (do not remove)
    { i: 'activity-reoccurrence-kpi', x: 0, y: 0, w: 12, h: 6 },
    { i: 'edge-time-kpi', x: 0, y: 0, w: 12, h: 6 },
    { i: 'lifecycle-time-kpi', x: 0, y: 0, w: 12, h: 6 },
    { i: 'activity-sequence-builder', x: 0, y: 0, w: 12, h: 12 },
    { i: 'edge-duration', x: 0, y: 0, w: 12, h: 6 },
    { i: 'pre-activity-selector', x: 0, y: 0, w: 12, h: 12 },
    { i: 'attr-stats', x: 0, y: 0, w: 12, h: 6, minH: 2 },
    { i: 'avg-events-per-object-type', x: 0, y: 0, w: 12, h: 8 },
    { i: 'cache-settings', x: 0, y: 0, w: 12, h: 8 },
    { i: 'count-total-events', x: 0, y: 0, w: 12, h: 4 },
    { i: 'count-total-objects', x: 0, y: 0, w: 12, h: 4 },
    { i: 'count-object-types', x: 0, y: 0, w: 12, h: 4 },
    { i: 'count-event-types', x: 0, y: 0, w: 12, h: 4 },
    { i: 'count-time-span', x: 0, y: 0, w: 12, h: 4 },
    { i: 'date-deviation-kpi', x: 0, y: 0, w: 12, h: 6 },
    { i: 'date-deviation-kpi-2', x: 0, y: 0, w: 12, h: 6 },
    { i: 'date-deviation-kpi-3', x: 0, y: 0, w: 12, h: 6 },
    { i: 'date-deviation-kpi-4', x: 0, y: 0, w: 12, h: 6 },
    { i: 'edge-card', x: 0, y: 0, w: 12, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 0, w: 12, h: 8 },
    { i: 'event-types-per-object-type', x: 0, y: 0, w: 12, h: 8 },
    { i: 'global-filter-demo', x: 0, y: 0, w: 12, h: 6 },
    { i: 'histogram', x: 0, y: 0, w: 12, h: 10 },
    { i: 'missing-activities', x: 0, y: 0, w: 12, h: 8 },
    { i: 'events-without-objects', x: 0, y: 0, w: 12, h: 8 },
    { i: 'missing-object-types', x: 0, y: 0, w: 12, h: 8 },
    { i: 'count-missing-timestamps', x: 0, y: 0, w: 12, h: 8 },
    { i: 'object-details', x: 0, y: 0, w: 12, h: 8 },
    { i: 'object-type-combinations', x: 0, y: 0, w: 12, h: 10 },
    { i: 'object-type-frequencies', x: 0, y: 0, w: 12, h: 8 },
    { i: 'orphan-objects', x: 0, y: 0, w: 12, h: 8 },
    { i: 'prozent-card', x: 0, y: 0, w: 12, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 0, w: 12, h: 8 },
    { i: 'variant-selector', x: 0, y: 0, w: 12, h: 14 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 12, h: 17 },
    { i: 'weekly-pattern', x: 0, y: 0, w: 12, h: 8 },

    // Activity Sequence Builder tab components
    { i: 'pre-activity-selector', x: 0, y: 0, w: 12, h: 12 },

    // Variants tab components
    { i: 'variant-selector', x: 0, y: 0, w: 12, h: 12 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 12, h: 12 },

    // Misc tab components
    // root-cause-analysis removed - rendered outside grid as static component

    // KPI tiles for leadTime tab (split into small tiles)
    { i: 'count-total-events', x: 0, y: 27, w: 3, h: 3 },
    { i: 'count-total-objects', x: 3, y: 27, w: 3, h: 3 },
    { i: 'count-object-types', x: 6, y: 27, w: 3, h: 3 },
    { i: 'count-event-types', x: 9, y: 27, w: 3, h: 3 },
    { i: 'count-time-span', x: 0, y: 30, w: 6, h: 3 },
    { i: 'count-missing-timestamps', x: 0, y: 33, w: 3, h: 3 },
    { i: 'missing-activities', x: 3, y: 33, w: 3, h: 3 },
    { i: 'missing-object-types', x: 6, y: 33, w: 3, h: 3 },
    { i: 'events-without-objects', x: 9, y: 33, w: 3, h: 3 },
    { i: 'orphan-objects', x: 0, y: 36, w: 3, h: 3 },

    // Additional KPI tiles
    { i: 'event-types-per-object-type', x: 6, y: 30, w: 6, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 39, w: 6, h: 6 },
    { i: 'activity-counts', x: 6, y: 39, w: 6, h: 6 },
    { i: 'object-type-combinations', x: 0, y: 45, w: 12, h: 6 },
    { i: 'object-type-frequencies', x: 0, y: 51, w: 12, h: 6 },
  ],
  md: [
    // Home tab components - attr-stats top left, histogram below, date deviation KPIs to the right in 2 columns
    { i: 'attr-stats', x: 0, y: 0, w: 5, h: 3, minH: 2, maxH: 12 },
    { i: 'histogram', x: 0, y: 3, w: 5, h: 7 },
    { i: 'date-deviation-kpi', x: 5, y: 0, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-2', x: 5, y: 4, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-3', x: 5, y: 8, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-4', x: 5, y: 12, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 10, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'prozent-card', x: 0, y: 14, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'edge-card', x: 5, y: 16, w: 5, h: 4, minH: 3, maxH: 12 },
    { i: 'weekly-pattern', x: 0, y: 20, w: 10, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 26, w: 10, h: 6 },
    { i: 'global-filter-demo', x: 0, y: 32, w: 5, h: 5 },
    { i: 'cache-settings', x: 5, y: 32, w: 5, h: 6 },
    { i: 'avg-events-per-object-type', x: 0, y: 38, w: 5, h: 6 },
    { i: 'object-details', x: 5, y: 38, w: 5, h: 6 },
    { i: 'edge-time-kpi', x: 0, y: 44, w: 5, h: 6, minH: 3, maxH: 12 },
    // activity-frequency-chart removed in this local branch
    { i: 'trace-pivot-table', x: 0, y: 50, w: 10, h: 10, minW: 4, minH: 6 },

    // Conformance
    { i: 'visual-conformance-checker', x: 0, y: 0, w: 10, h: 14, minW: 6, minH: 6 },

    // Test tab components - single full-width component for isolated testing
    { i: 'trace-pivot-table', x: 0, y: 0, w: 10, h: 10, minW: 4, minH: 6 },
    { i: 'activity-counts', x: 0, y: 0, w: 10, h: 8 },
    // activity-frequency-ratio-kpi remains available in test tab (do not remove)
    { i: 'activity-reoccurrence-kpi', x: 0, y: 0, w: 10, h: 6 },
    { i: 'pre-activity-selector', x: 0, y: 0, w: 10, h: 12 },
    { i: 'attr-stats', x: 0, y: 0, w: 10, h: 6, minH: 2 },
    { i: 'avg-events-per-object-type', x: 0, y: 0, w: 10, h: 8 },
    { i: 'cache-settings', x: 0, y: 0, w: 10, h: 8 },
    { i: 'count-total-events', x: 0, y: 0, w: 10, h: 4 },
    { i: 'count-total-objects', x: 0, y: 0, w: 10, h: 4 },
    { i: 'count-object-types', x: 0, y: 0, w: 10, h: 4 },
    { i: 'count-event-types', x: 0, y: 0, w: 10, h: 4 },
    { i: 'count-time-span', x: 0, y: 0, w: 10, h: 4 },
    { i: 'date-deviation-kpi', x: 0, y: 0, w: 10, h: 6 },
    { i: 'date-deviation-kpi-2', x: 0, y: 0, w: 10, h: 6 },
    { i: 'date-deviation-kpi-3', x: 0, y: 0, w: 10, h: 6 },
    { i: 'date-deviation-kpi-4', x: 0, y: 0, w: 10, h: 6 },
    { i: 'edge-card', x: 0, y: 0, w: 10, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 0, w: 10, h: 8 },
    { i: 'event-types-per-object-type', x: 0, y: 0, w: 10, h: 8 },
    { i: 'global-filter-demo', x: 0, y: 0, w: 10, h: 6 },
    { i: 'histogram', x: 0, y: 0, w: 10, h: 10 },
    { i: 'missing-activities', x: 0, y: 0, w: 10, h: 8 },
    { i: 'events-without-objects', x: 0, y: 0, w: 10, h: 8 },
    { i: 'missing-object-types', x: 0, y: 0, w: 10, h: 8 },
    { i: 'count-missing-timestamps', x: 0, y: 0, w: 10, h: 8 },
    { i: 'object-details', x: 0, y: 0, w: 10, h: 8 },
    { i: 'object-type-combinations', x: 0, y: 0, w: 10, h: 10 },
    { i: 'object-type-frequencies', x: 0, y: 0, w: 10, h: 8 },
    { i: 'orphan-objects', x: 0, y: 0, w: 10, h: 8 },
    { i: 'prozent-card', x: 0, y: 0, w: 10, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 0, w: 10, h: 8 },
    { i: 'variant-selector', x: 0, y: 0, w: 10, h: 12 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 10, h: 12 },
    { i: 'weekly-pattern', x: 0, y: 0, w: 10, h: 8 },

    // Activity Sequence Builder tab components
    { i: 'pre-activity-selector', x: 0, y: 0, w: 10, h: 12 },

    // Variants tab components
    { i: 'variant-selector', x: 0, y: 0, w: 10, h: 8 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 10, h: 8 },

    // Misc tab components
    // root-cause-analysis removed - rendered outside grid as static component

    // KPI tiles for leadTime tab (split into small tiles)
    { i: 'count-total-events', x: 0, y: 44, w: 5, h: 3 },
    { i: 'count-total-objects', x: 5, y: 44, w: 5, h: 3 },
    { i: 'count-object-types', x: 0, y: 47, w: 5, h: 3 },
    { i: 'count-event-types', x: 5, y: 47, w: 5, h: 3 },
    { i: 'count-time-span', x: 0, y: 50, w: 10, h: 3 },
    { i: 'count-missing-timestamps', x: 0, y: 53, w: 5, h: 3 },
    { i: 'missing-activities', x: 5, y: 53, w: 5, h: 3 },
    { i: 'missing-object-types', x: 0, y: 56, w: 5, h: 3 },
    { i: 'events-without-objects', x: 5, y: 56, w: 5, h: 3 },
    { i: 'orphan-objects', x: 0, y: 59, w: 5, h: 3 },

    // Additional KPI tiles
    { i: 'event-types-per-object-type', x: 0, y: 62, w: 10, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 68, w: 5, h: 6 },
    { i: 'activity-counts', x: 5, y: 68, w: 5, h: 6 },
    { i: 'object-type-combinations', x: 0, y: 74, w: 10, h: 6 },
    { i: 'object-type-frequencies', x: 0, y: 80, w: 10, h: 6 },
  ],
  sm: [
    // Home tab components - removed height restrictions for dynamic sizing
    { i: 'histogram', x: 0, y: 0, w: 6, h: 7 },
    { i: 'attr-stats', x: 0, y: 7, w: 6, h: 3, minH: 2, maxH: 12 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 10, w: 3, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi', x: 3, y: 10, w: 3, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-2', x: 0, y: 14, w: 3, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-3', x: 3, y: 14, w: 3, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-4', x: 0, y: 18, w: 3, h: 4, minH: 3, maxH: 12 },
    { i: 'prozent-card', x: 3, y: 18, w: 3, h: 4, minH: 3, maxH: 12 },
    { i: 'edge-card', x: 0, y: 22, w: 6, h: 4, minH: 3, maxH: 12 },
    { i: 'weekly-pattern', x: 0, y: 26, w: 6, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 32, w: 6, h: 6 },
    { i: 'global-filter-demo', x: 0, y: 38, w: 3, h: 5 },
    { i: 'cache-settings', x: 3, y: 38, w: 3, h: 6 },
    { i: 'avg-events-per-object-type', x: 0, y: 44, w: 3, h: 6 },
    { i: 'object-details', x: 3, y: 44, w: 3, h: 6 },
    { i: 'trace-pivot-table', x: 0, y: 50, w: 6, h: 10, minW: 4, minH: 6 },

    // Conformance
    { i: 'visual-conformance-checker', x: 0, y: 0, w: 6, h: 13, minW: 4, minH: 5 },

    // Test tab components - single full-width component for isolated testing
    { i: 'trace-pivot-table', x: 0, y: 0, w: 6, h: 10, minW: 4, minH: 6 },
    { i: 'activity-counts', x: 0, y: 0, w: 6, h: 8 },
    { i: 'activity-frequency-ratio-kpi', x: 0, y: 0, w: 6, h: 6 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 0, w: 6, h: 6 },
    { i: 'pre-activity-selector', x: 0, y: 0, w: 6, h: 12 },
    { i: 'attr-stats', x: 0, y: 0, w: 6, h: 6, minH: 2 },
    { i: 'avg-events-per-object-type', x: 0, y: 0, w: 6, h: 8 },
    { i: 'cache-settings', x: 0, y: 0, w: 6, h: 8 },
    { i: 'count-total-events', x: 0, y: 0, w: 6, h: 4 },
    { i: 'count-total-objects', x: 0, y: 0, w: 6, h: 4 },
    { i: 'count-object-types', x: 0, y: 0, w: 6, h: 4 },
    { i: 'count-event-types', x: 0, y: 0, w: 6, h: 4 },
    { i: 'count-time-span', x: 0, y: 0, w: 6, h: 4 },
    { i: 'date-deviation-kpi', x: 0, y: 0, w: 6, h: 6 },
    { i: 'date-deviation-kpi-2', x: 0, y: 0, w: 6, h: 6 },
    { i: 'date-deviation-kpi-3', x: 0, y: 0, w: 6, h: 6 },
    { i: 'date-deviation-kpi-4', x: 0, y: 0, w: 6, h: 6 },
    { i: 'edge-card', x: 0, y: 0, w: 6, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 0, w: 6, h: 8 },
    { i: 'event-types-per-object-type', x: 0, y: 0, w: 6, h: 8 },
    { i: 'global-filter-demo', x: 0, y: 0, w: 6, h: 6 },
    { i: 'histogram', x: 0, y: 0, w: 6, h: 10 },
    { i: 'missing-activities', x: 0, y: 0, w: 6, h: 8 },
    { i: 'events-without-objects', x: 0, y: 0, w: 6, h: 8 },
    { i: 'missing-object-types', x: 0, y: 0, w: 6, h: 8 },
    { i: 'count-missing-timestamps', x: 0, y: 0, w: 6, h: 8 },
    { i: 'object-details', x: 0, y: 0, w: 6, h: 8 },
    { i: 'object-type-combinations', x: 0, y: 0, w: 6, h: 10 },
    { i: 'object-type-frequencies', x: 0, y: 0, w: 6, h: 8 },
    { i: 'orphan-objects', x: 0, y: 0, w: 6, h: 8 },
    { i: 'prozent-card', x: 0, y: 0, w: 6, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 0, w: 6, h: 8 },
    { i: 'variant-selector', x: 0, y: 0, w: 6, h: 12 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 6, h: 12 },
    { i: 'weekly-pattern', x: 0, y: 0, w: 6, h: 8 },

    // Activity Sequence Builder tab components
    { i: 'pre-activity-selector', x: 0, y: 0, w: 6, h: 12 },

    // Variants tab components
    { i: 'variant-selector', x: 0, y: 0, w: 6, h: 8 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 6, h: 8 },

    // Misc tab components
    // root-cause-analysis removed - rendered outside grid as static component

    // KPI tiles for leadTime tab (split into small tiles)
    { i: 'count-total-events', x: 0, y: 50, w: 3, h: 3 },
    { i: 'count-total-objects', x: 3, y: 50, w: 3, h: 3 },
    { i: 'count-object-types', x: 0, y: 53, w: 3, h: 3 },
    { i: 'count-event-types', x: 3, y: 53, w: 3, h: 3 },
    { i: 'count-time-span', x: 0, y: 56, w: 6, h: 3 },
    { i: 'count-missing-timestamps', x: 0, y: 59, w: 3, h: 3 },
    { i: 'missing-activities', x: 3, y: 59, w: 3, h: 3 },
    { i: 'missing-object-types', x: 0, y: 62, w: 3, h: 3 },
    { i: 'events-without-objects', x: 3, y: 62, w: 3, h: 3 },
    { i: 'orphan-objects', x: 0, y: 65, w: 3, h: 3 },

    // Additional KPI tiles
    { i: 'event-types-per-object-type', x: 0, y: 68, w: 6, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 74, w: 3, h: 6 },
    { i: 'activity-counts', x: 3, y: 74, w: 3, h: 6 },
    { i: 'object-type-combinations', x: 0, y: 80, w: 6, h: 6 },
    { i: 'object-type-frequencies', x: 0, y: 86, w: 6, h: 6 },
  ],
  xs: [
    // Home tab components - removed height restrictions for dynamic sizing
    { i: 'histogram', x: 0, y: 0, w: 4, h: 7 },
    { i: 'attr-stats', x: 0, y: 7, w: 4, h: 3, minH: 2, maxH: 12 },
    { i: 'date-deviation-kpi', x: 0, y: 10, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-2', x: 2, y: 10, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-3', x: 0, y: 14, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-4', x: 2, y: 14, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 18, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'prozent-card', x: 2, y: 18, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'edge-card', x: 0, y: 22, w: 4, h: 4, minH: 3, maxH: 12 },
    { i: 'weekly-pattern', x: 0, y: 26, w: 4, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 32, w: 4, h: 6 },
    { i: 'global-filter-demo', x: 0, y: 38, w: 2, h: 5 },
    { i: 'cache-settings', x: 2, y: 38, w: 2, h: 6 },
    { i: 'avg-events-per-object-type', x: 0, y: 44, w: 2, h: 6 },
    { i: 'object-details', x: 2, y: 44, w: 2, h: 6 },
    { i: 'trace-pivot-table', x: 0, y: 50, w: 4, h: 10, minW: 4, minH: 6 },

    // Conformance
    { i: 'visual-conformance-checker', x: 0, y: 0, w: 4, h: 11, minW: 4, minH: 4 },

    // Test tab components - single full-width component for isolated testing
    { i: 'trace-pivot-table', x: 0, y: 0, w: 4, h: 10, minW: 4, minH: 6 },
    { i: 'activity-counts', x: 0, y: 0, w: 4, h: 8 },
    { i: 'activity-frequency-ratio-kpi', x: 0, y: 0, w: 4, h: 6 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 0, w: 4, h: 6 },
    { i: 'pre-activity-selector', x: 0, y: 0, w: 4, h: 12 },
    { i: 'attr-stats', x: 0, y: 0, w: 4, h: 6, minH: 2 },
    { i: 'avg-events-per-object-type', x: 0, y: 0, w: 4, h: 8 },
    { i: 'cache-settings', x: 0, y: 0, w: 4, h: 8 },
    { i: 'count-total-events', x: 0, y: 0, w: 4, h: 4 },
    { i: 'count-total-objects', x: 0, y: 0, w: 4, h: 4 },
    { i: 'count-object-types', x: 0, y: 0, w: 4, h: 4 },
    { i: 'count-event-types', x: 0, y: 0, w: 4, h: 4 },
    { i: 'count-time-span', x: 0, y: 0, w: 4, h: 4 },
    { i: 'date-deviation-kpi', x: 0, y: 0, w: 4, h: 6 },
    { i: 'date-deviation-kpi-2', x: 0, y: 0, w: 4, h: 6 },
    { i: 'date-deviation-kpi-3', x: 0, y: 0, w: 4, h: 6 },
    { i: 'date-deviation-kpi-4', x: 0, y: 0, w: 4, h: 6 },
    { i: 'edge-card', x: 0, y: 0, w: 4, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 0, w: 4, h: 8 },
    { i: 'event-types-per-object-type', x: 0, y: 0, w: 4, h: 8 },
    { i: 'global-filter-demo', x: 0, y: 0, w: 4, h: 6 },
    { i: 'histogram', x: 0, y: 0, w: 4, h: 10 },
    { i: 'missing-activities', x: 0, y: 0, w: 4, h: 8 },
    { i: 'events-without-objects', x: 0, y: 0, w: 4, h: 8 },
    { i: 'missing-object-types', x: 0, y: 0, w: 4, h: 8 },
    { i: 'count-missing-timestamps', x: 0, y: 0, w: 4, h: 8 },
    { i: 'object-details', x: 0, y: 0, w: 4, h: 8 },
    { i: 'object-type-combinations', x: 0, y: 0, w: 4, h: 10 },
    { i: 'object-type-frequencies', x: 0, y: 0, w: 4, h: 8 },
    { i: 'orphan-objects', x: 0, y: 0, w: 4, h: 8 },
    { i: 'prozent-card', x: 0, y: 0, w: 4, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 0, w: 4, h: 8 },
    { i: 'variant-selector', x: 0, y: 0, w: 4, h: 12 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 4, h: 12 },
    { i: 'weekly-pattern', x: 0, y: 0, w: 4, h: 8 },

    // Activity Sequence Builder tab components
    { i: 'pre-activity-selector', x: 0, y: 0, w: 4, h: 12 },

    // Variants tab components
    { i: 'variant-selector', x: 0, y: 0, w: 4, h: 8 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 4, h: 8 },

    // Misc tab components
    // root-cause-analysis removed - rendered outside grid as static component

    // KPI tiles for leadTime tab (split into small tiles)
    { i: 'count-total-events', x: 0, y: 50, w: 2, h: 3 },
    { i: 'count-total-objects', x: 2, y: 50, w: 2, h: 3 },
    { i: 'count-object-types', x: 0, y: 53, w: 2, h: 3 },
    { i: 'count-event-types', x: 2, y: 53, w: 2, h: 3 },
    { i: 'count-time-span', x: 0, y: 56, w: 4, h: 3 },
    { i: 'count-missing-timestamps', x: 0, y: 59, w: 2, h: 3 },
    { i: 'missing-activities', x: 2, y: 59, w: 2, h: 3 },
    { i: 'missing-object-types', x: 0, y: 62, w: 2, h: 3 },
    { i: 'events-without-objects', x: 2, y: 62, w: 2, h: 3 },
    { i: 'orphan-objects', x: 0, y: 65, w: 2, h: 3 },

    // Additional KPI tiles
    { i: 'event-types-per-object-type', x: 0, y: 68, w: 4, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 74, w: 2, h: 6 },
    { i: 'activity-counts', x: 2, y: 74, w: 2, h: 6 },
    { i: 'object-type-combinations', x: 0, y: 80, w: 4, h: 6 },
    { i: 'object-type-frequencies', x: 0, y: 86, w: 4, h: 6 },
  ],
  xxs: [
    // Home tab components - removed height restrictions for dynamic sizing
    { i: 'histogram', x: 0, y: 0, w: 2, h: 7 },
    { i: 'attr-stats', x: 0, y: 7, w: 2, h: 3, minH: 2, maxH: 12 },
    { i: 'date-deviation-kpi', x: 0, y: 10, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-2', x: 0, y: 14, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-3', x: 0, y: 18, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'date-deviation-kpi-4', x: 0, y: 22, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 26, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'prozent-card', x: 0, y: 30, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'edge-card', x: 0, y: 34, w: 2, h: 4, minH: 3, maxH: 12 },
    { i: 'weekly-pattern', x: 0, y: 38, w: 2, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 44, w: 2, h: 6 },
    { i: 'global-filter-demo', x: 0, y: 50, w: 2, h: 5 },
    { i: 'cache-settings', x: 0, y: 55, w: 2, h: 6 },
    { i: 'avg-events-per-object-type', x: 0, y: 61, w: 2, h: 6 },
    { i: 'object-details', x: 0, y: 67, w: 2, h: 6 },
    { i: 'trace-pivot-table', x: 0, y: 73, w: 2, h: 10, minH: 6 },

    // Conformance
    { i: 'visual-conformance-checker', x: 0, y: 0, w: 2, h: 10, minW: 2, minH: 4 },

    // Test tab components - single full-width component for isolated testing
    { i: 'trace-pivot-table', x: 0, y: 0, w: 2, h: 10, minH: 6 },
    { i: 'activity-counts', x: 0, y: 0, w: 2, h: 8 },
    { i: 'activity-frequency-ratio-kpi', x: 0, y: 0, w: 2, h: 6 },
    { i: 'activity-reoccurrence-kpi', x: 0, y: 0, w: 2, h: 6 },
    { i: 'pre-activity-selector', x: 0, y: 0, w: 2, h: 12 },
    { i: 'attr-stats', x: 0, y: 0, w: 2, h: 6, minH: 2 },
    { i: 'avg-events-per-object-type', x: 0, y: 0, w: 2, h: 8 },
    { i: 'cache-settings', x: 0, y: 0, w: 2, h: 8 },
    { i: 'count-total-events', x: 0, y: 0, w: 2, h: 4 },
    { i: 'count-total-objects', x: 0, y: 0, w: 2, h: 4 },
    { i: 'count-object-types', x: 0, y: 0, w: 2, h: 4 },
    { i: 'count-event-types', x: 0, y: 0, w: 2, h: 4 },
    { i: 'count-time-span', x: 0, y: 0, w: 2, h: 4 },
    { i: 'date-deviation-kpi', x: 0, y: 0, w: 2, h: 6 },
    { i: 'date-deviation-kpi-2', x: 0, y: 0, w: 2, h: 6 },
    { i: 'date-deviation-kpi-3', x: 0, y: 0, w: 2, h: 6 },
    { i: 'date-deviation-kpi-4', x: 0, y: 0, w: 2, h: 6 },
    { i: 'edge-card', x: 0, y: 0, w: 2, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 0, w: 2, h: 8 },
    { i: 'event-types-per-object-type', x: 0, y: 0, w: 2, h: 8 },
    { i: 'global-filter-demo', x: 0, y: 0, w: 2, h: 6 },
    { i: 'histogram', x: 0, y: 0, w: 2, h: 10 },
    { i: 'missing-activities', x: 0, y: 0, w: 2, h: 8 },
    { i: 'events-without-objects', x: 0, y: 0, w: 2, h: 8 },
    { i: 'missing-object-types', x: 0, y: 0, w: 2, h: 8 },
    { i: 'count-missing-timestamps', x: 0, y: 0, w: 2, h: 8 },
    { i: 'object-details', x: 0, y: 0, w: 2, h: 8 },
    { i: 'object-type-combinations', x: 0, y: 0, w: 2, h: 10 },
    { i: 'object-type-frequencies', x: 0, y: 0, w: 2, h: 8 },
    { i: 'orphan-objects', x: 0, y: 0, w: 2, h: 8 },
    { i: 'prozent-card', x: 0, y: 0, w: 2, h: 6 },
    { i: 'timeline-metrics', x: 0, y: 0, w: 2, h: 8 },
    { i: 'variant-selector', x: 0, y: 0, w: 2, h: 12 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 2, h: 12 },
    { i: 'weekly-pattern', x: 0, y: 0, w: 2, h: 8 },

    // Activity Sequence Builder tab components
    { i: 'pre-activity-selector', x: 0, y: 0, w: 2, h: 12 },

    // Variants tab components
    { i: 'variant-selector', x: 0, y: 0, w: 2, h: 8 },
    { i: 'variant-selector-extended', x: 0, y: 0, w: 2, h: 8 },

    // Misc tab components
    // root-cause-analysis removed - rendered outside grid as static component

    // KPI tiles for leadTime tab (split into small tiles)
    { i: 'count-total-events', x: 0, y: 73, w: 2, h: 3 },
    { i: 'count-total-objects', x: 0, y: 76, w: 2, h: 3 },
    { i: 'count-object-types', x: 0, y: 79, w: 2, h: 3 },
    { i: 'count-event-types', x: 0, y: 82, w: 2, h: 3 },
    { i: 'count-time-span', x: 0, y: 85, w: 2, h: 3 },
    { i: 'count-missing-timestamps', x: 0, y: 88, w: 1, h: 3 },
    { i: 'missing-activities', x: 1, y: 88, w: 1, h: 3 },
    { i: 'missing-object-types', x: 0, y: 91, w: 1, h: 3 },
    { i: 'events-without-objects', x: 1, y: 91, w: 1, h: 3 },
    { i: 'orphan-objects', x: 0, y: 94, w: 1, h: 3 },

    // Additional KPI tiles
    { i: 'event-types-per-object-type', x: 0, y: 97, w: 2, h: 6 },
    { i: 'object-type-event-intensity', x: 0, y: 103, w: 1, h: 6 },
    { i: 'activity-counts', x: 1, y: 103, w: 1, h: 6 },
    { i: 'object-type-combinations', x: 0, y: 109, w: 2, h: 6 },
    { i: 'object-type-frequencies', x: 0, y: 115, w: 2, h: 6 },
  ]
};

function App() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activityName, setActivityName] = useState('');
  const [objectType, setObjectType] = useState('');
  const [activeFile, setActiveFile] = useState(null);
  // Tenant tile allowlist state. undefined = not yet known; null = confirmed
  // unrestricted; Set = confirmed restricted to these (alias-normalized) ids.
  // Fed solely by AllowedComponentIdsResolver (rendered inside <AppProviders>
  // further down), which reads OCELFileManager's own existing metadata fetch —
  // no separate fetch here, no blocking on isInitializing. Every read site
  // (ComponentLibrary, the grid-render filters, applyConfig) uses `== null`,
  // which is true for both `undefined` and `null` — see the comment on
  // AllowedComponentIdsResolver above for why "not yet known" deliberately
  // renders identically to "unrestricted" rather than failing closed.
  const [allowedComponentIds, setAllowedComponentIds] = useState(undefined);
  const [isInitializing, setIsInitializing] = useState(true);
  // Blocking init-failure message shown instead of the dashboard when no
  // dataset could be resolved and the user has no /projects access to fix it
  // themselves (see the fetchActiveFile effect below) — avoids the bounce
  // loop where navigate('/projects') would just be redirected straight back
  // here by ProtectedRoute.
  const [initError, setInitError] = useState(null);
  // Single source of truth: WorkshopModeProvider (mounted in index.jsx, above
  // this component in the tree). Do NOT re-add a local localStorage copy here
  // — that previously drifted to a different (visible-by-default) default
  // than the shared context, which is exactly the bug this fixed.
  const { workshopMode } = useWorkshopMode();
  const { t } = useTranslation('common');

  // Tab management
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [autoEditTabId, setAutoEditTabId] = useState(null);

  // Active tab is driven by the URL ?tab= param; fall back to the first available tab if missing/invalid
  // (can't hardcode 'home' — it's just a regular tab now and may have been removed)
  const activeTab = useMemo(() => {
    const tabFromUrl = searchParams.get('tab');
    return (tabFromUrl && tabs.some(t => t.id === tabFromUrl)) ? tabFromUrl : tabs[0]?.id;
  }, [searchParams, tabs]);

  // Updating the active tab updates the URL (preserves any other search params)
  const setActiveTab = useCallback((tabId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    });
  }, [setSearchParams]);

  // Ensure the URL always shows the current tab (initial load without ?tab=)
  useEffect(() => {
    if (!searchParams.get('tab') && tabs[0]?.id) {
      setSearchParams({ tab: tabs[0].id }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeDisplayName, setActiveDisplayName] = useState(null);

  const handleDisplayNameChange = useCallback((displayName) => {
    setActiveDisplayName(displayName);
  }, []);

  // Keep ?file= in sync with the slugified display name of the active file
  useEffect(() => {
    if (!activeFile) return;
    const slug = activeDisplayName
      ? activeDisplayName.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : activeFile;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('file', slug);
      return next;
    }, { replace: true });
  }, [activeFile, activeDisplayName, setSearchParams]);

  // Unified component management - all components in one list
  const [allComponents, setAllComponents] = useState(DEFAULT_COMPONENTS);

  // Dashboard config persistence — keyed by user ID so each Supabase user
  // gets their own isolated localStorage entry on a shared browser.
  const { user, canAccessProjects } = useAuth();
  const { loadConfig, saveConfig, syncFromServerIfNewer, isSaving, saveError } = useDashboardConfig(user?.id);
  // Ref keyed by instanceId → () => settings; populated by each rendered component
  const componentSettingsGetters = useRef({});
  // Dedicated getter for the always-present ReactFlowGraph singleton (not in allComponents)
  const reactFlowGraphSettingsGetter = useRef(null);
  // Settings loaded from a saved config and passed down to components as initialSettings
  const [restoredComponentSettings, setRestoredComponentSettings] = useState({});
  // Bumped whenever a config is applied to an already-mounted dashboard (Config
  // Manager Load/Import) so tiles remount and re-run their mount-only restore
  // effect instead of silently keeping stale view state (e.g. granularity).
  const [configEpoch, setConfigEpoch] = useState(0);

  // Refresh functions stored in a ref — always read at click-time so
  // the latest callback is used regardless of parent render cycle.
  const componentRefreshFunctions = React.useRef({});
  const [componentHeaderActions, setComponentHeaderActions] = React.useState({});

  // Store custom titles for components
  const [componentTitles, setComponentTitles] = useState({});

  // Handle title changes from components
  const handleTitleChange = useCallback((componentId, newTitle) => {
    setComponentTitles(prev => ({
      ...prev,
      [componentId]: newTitle
    }));
  }, []);

  // Handle file changes from the file manager
  const handleFileChange = useCallback((fileType) => {
    setStoredActiveFile(fileType); // persist the per-browser selection
    setActiveFile(fileType);
    // This will trigger re-renders of components that depend on the active file
  }, []);

  const handleKennwortDataLoaded = useCallback((data, isLoading, error) => {
      console.log("Kennwort Data Received for potential display:", { data, isLoading, error });
  }, []);

  // Handle adding new components
  const handleComponentAdd = useCallback((component) => {
    const newComponent = {
      ...component,
      instanceId: `${component.id}-${Date.now()}`, // Unique instance ID
      tab: activeTab, // Assign to current tab
      type: 'dynamic' // Mark as user-added
    };

    setAllComponents(prev => [...prev, newComponent]);
  }, [activeTab]);

  // Handle removing components
  const handleComponentRemove = useCallback((componentToRemove) => {
    const removedId = componentToRemove.instanceId || componentToRemove.id;
    delete componentRefreshFunctions.current[removedId];
    delete componentSettingsGetters.current[removedId];
    setComponentHeaderActions((prev) => {
      const { [removedId]: _, ...rest } = prev;
      return rest;
    });

    setAllComponents(prev =>
      prev.filter(comp => {
        // For dynamic components, match by instanceId
        if (componentToRemove.instanceId) {
          return comp.instanceId !== componentToRemove.instanceId;
        }
        // For static components, match by id and type
        return !(comp.id === componentToRemove.id && comp.type === componentToRemove.type);
      })
    );
  }, []);

  // Handle creating new custom tabs
  const handleCreateTab = useCallback(() => {
    const newTabId = `custom-${Date.now()}`;
    const newTab = createTab(newTabId, 'Custom', { showProcessGraph: false });

    setTabs(prev => [...prev, newTab]);
    setActiveTab(newTabId); // Switch to the new tab
    setAutoEditTabId(newTabId); // Trigger inline edit mode in sidebar
  }, []);

  // Handle renaming custom tabs
  const handleRenameTab = useCallback((tabId, newLabel) => {
    if (!newLabel.trim()) return;
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, label: newLabel.trim() } : t));
  }, []);

  // Handle toggling the process graph on/off for a specific tab
  const handleToggleProcessGraph = useCallback((tabId) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, showProcessGraph: !t.showProcessGraph } : t));
  }, []);

  // Handle assigning/updating filters scoped to a specific tab
  const handleUpdateTabFilters = useCallback((tabId, tabFilters) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, tabFilters } : t));
  }, []);

  // Handle removing tabs (at least one tab must always remain)
  const handleRemoveTab = useCallback((tabId) => {
    if (tabs.length <= 1) return;

    setTabs(prev => prev.filter(tab => tab.id !== tabId));

    // Remove components belonging to this tab
    setAllComponents(prev => prev.filter(comp => comp.tab !== tabId));

    // If we're removing the active tab, switch to the first tab
    if (activeTab === tabId) {
      const remainingTabs = tabs.filter(tab => tab.id !== tabId);
      if (remainingTabs.length > 0) {
        setActiveTab(remainingTabs[0].id);
      }
    }
  }, [activeTab, tabs]);

  // Handle drag-and-drop tab reordering. Array position IS the order (no
  // separate `order` field), so this is a plain state replacement. Guarded
  // here too (not just at the drag-sensor level in AppSidebar) so that any
  // future caller of this handler can't bypass the Workshop Mode restriction.
  const handleReorderTabs = useCallback((newOrderedTabs) => {
    if (workshopMode) return;
    setTabs(newOrderedTabs);
  }, [workshopMode]);

  // Render component based on component ID
  const renderComponent = useCallback((component) => {
    const componentId = component.instanceId || component.id;

    const props = {
      activeFile,
      activityName,
      objectType,
      onKennwortDataLoaded: handleKennwortDataLoaded,
      onRefresh: (refreshFn) => {
        componentRefreshFunctions.current[componentId] = refreshFn;
      },
      onGetHeaderActions: (headerActionsFn) => {
        // Store the header actions for this component — use functional update
        // with an identity check so that unchanged JSX doesn't cause re-renders.
        setComponentHeaderActions(prev => {
          if (prev[componentId] === headerActionsFn) return prev;
          return { ...prev, [componentId]: headerActionsFn };
        });
      },
      onTitleChange: (newTitle) => {
        // Handle title changes from components
        handleTitleChange(componentId, newTitle);
      },
      onRegisterSettingsGetter: (getter) => {
        componentSettingsGetters.current[componentId] = getter;
      },
      initialSettings: restoredComponentSettings[componentId] ?? null,
    };

    switch (component.id) {
      case 'resource-calendar':
        return <ResourceCalendar {...props} />;
      case 'histogram': // Backwards compatibility alias
      case 'lead-time-histogram':
        return <LeadTimeHistogram {...props} defaultObjectType="Kundenauftragsposition" />;
      case 'edge-duration-histogram':
        return <EdgeDurationHistogram {...props} />;
      case 'time-span-tile':
        return <TimeSpanTile {...props} />;
      case 'lead-time-dashboard':
        return <LeadTimeDashboard {...props} />;
      case 'attr-stats':
      case 'lead-time-statistics':
        return <LeadTimeStatistics {...props} defaultObjectType="Kundenauftragsposition" />;
      case 'attribute-table':
        return <AttributeTable {...props} />;
      case 'trace-pivot-table':
        return <TracePivotTable {...props} />;
      case 'gantt-chart':
        return <GanttChart {...props} />;
      case 'biaxial-scatter-plot':
        return <BiaxialScatterPlot {...props} />;
      case 'object-table':
        return <ObjectTable {...props} />;
      case 'aggregation-kpi':
        return <AggregationKPI {...props} />;
      case 'efficiency-kpi':
        return <EfficiencyKPI {...props} />;
      case 'object-count-kpi':
        return <ObjectCountKPI {...props} />;
      case 'object-conversion-kpi':
        return <ObjectConversionKPI {...props} />;
      case 'automation-potential-kpi':
        return <AutomationPotentialKPI {...props} />;
      case 'activity-reoccurrence-kpi':
        return <ActivityReoccurrenceKPI {...props} />;
      case 'activity-frequency-ratio-kpi':
        return <ActivityFrequencyRatioKPI {...props} />;
      case 'edge-duration': // Backwards compatibility alias
      case 'edge-time-kpi':
        return <EdgeDuration {...props} />;
      case 'lifecycle-time-kpi':
        return <LifecycleTimeKPI {...props} />;
      case 'date-deviation-kpi':
        return <DateDeviationKPI
          {...props}
          defaultKpiTitle="Terminänderung durch Vertrieb | Vorverlegung"
          defaultActivity="Terminänderung durch den Vertrieb"
          defaultDateAttribute1="Aktueller Termin"
          defaultDateAttribute2="Alter Termin"
          defaultCondition="less"
          defaultAggregation="average"
        />;
      case 'date-deviation-kpi-custom':
        return <DateDeviationKPI {...props} />;
      case 'liefertermintreue-kpi':
        return <LiefertermintreueKPI {...props} />;
      case 'object-count-by-activity-kpi':
        return <ObjectCountByActivityKPI {...props} />;
      case 'date-deviation-kpi-2':
        return <DateDeviationKPI
          {...props}
          defaultKpiTitle="Terminänderung durch Vertrieb | Verspätung"
          defaultActivity="Terminänderung durch den Vertrieb"
          defaultDateAttribute1="Aktueller Termin"
          defaultDateAttribute2="Alter Termin"
          defaultCondition="greater"
          defaultAggregation="average"
        />;
      case 'date-deviation-kpi-3':
        return <DateDeviationKPI
          {...props}
          defaultKpiTitle="Terminänderung durch AZ | Vorverlegung"
          defaultActivity="Terminänderung durch AZ"
          defaultDateAttribute1="Aktueller Termin"
          defaultDateAttribute2="Alter Termin"
          defaultCondition="less"
          defaultAggregation="average"
        />;
      case 'date-deviation-kpi-4':
        return <DateDeviationKPI
          {...props}
          defaultKpiTitle="Terminänderung durch AZ | Verspätung"
          defaultActivity="Terminänderung durch AZ"
          defaultDateAttribute1="Aktueller Termin"
          defaultDateAttribute2="Alter Termin"
          defaultCondition="greater"
          defaultAggregation="average"
        />;
      case 'edge-card': // Backwards compatibility alias
      case 'edge-frequency':
        return <EdgeFrequency {...props} />;
      case 'weekly-pattern': // Backwards compatibility alias
      case 'weekday-activity-count':
        return <WeekdayActivityCount {...props} />;
      case 'timeline-metrics': // Backwards compatibility alias
      case 'timeline-diagram':
        return <TimelineDiagram {...props} />;
      case 'activity-frequency-chart':
        return <ActivityFrequencyChart {...props} />;
      case 'total-downtime-kpi':
        return <TotalDowntimeKPI {...props} />;
      case 'resource-availability-kpi':
        return <ResourceUtilizationKPI {...props} />;
      case 'resource-utilization-chart':
        return <ResourceUtilizationChart {...props} />;
      case 'executive-resource-utilization-tile':
        return <ExecutiveResourceUtilizationTile {...props} />;
      case 'resource-status-monitor':
        return <ResourceStatusMonitor {...props} />;
      case 'attribute-sum-bar-chart':
        return <AttributeSumBarChart {...props} />;
      case 'attribute-sum-line-chart':
        return <AttributeSumLineChart {...props} />;
      case 'overdue-value-timeline':
        return <OverdueValueTimeline {...props} />;
      case 'liefertermintreue-timeline':
        return <LiefertermintreueTimeline {...props} />;
      case 'absolute-delay-timeline':
        return <AbsoluteDelayTimeline {...props} />;
      case 'durchlaufzeit-timeline':
        return <DurchlaufzeitTimeline {...props} />;
      case 'transition-time-line-chart':
        return <TransitionTimeLineChart {...props} />;
      case 'object-counts-by-selected-activities':
        return <ObjectCountsBySelectedActivities {...props} />;
      case 'variant-selector': // Backwards compatibility alias
      case 'variant-explorer':
        return <VariantExplorer {...props} />;
      case 'variant-selector-extended': // Backwards compatibility alias
      case 'variant-explorer-extended':
        return <VariantExplorerExtended {...props} />;
      case 'abc-xyz-analysis':
        return <ABCXYZAnalysisPanel {...props} />;
      case 'conformance-checker':
        return <VisualConformanceChecker {...props} />;
      case 'temporal-conformance-checker':
        return <VisualConformanceChecker {...props} />;
      case 'visual-conformance-checker':
        return <VisualConformanceChecker {...props} />;
      case 'count-total-events':
        return <CountsTiles activeFile={activeFile} single="totalEvents" onRefresh={props.onRefresh} />;
      case 'count-total-objects':
        return <CountsTiles activeFile={activeFile} single="totalObjects" onRefresh={props.onRefresh} />;
      case 'count-object-types':
        return <CountsTiles activeFile={activeFile} single="objectTypeCount" onRefresh={props.onRefresh} />;
      case 'count-event-types':
        return <CountsTiles activeFile={activeFile} single="eventTypeCount" onRefresh={props.onRefresh} />;
      case 'count-time-span':
        return <CountsTiles activeFile={activeFile} single="timeSpan" onRefresh={props.onRefresh} />;
      case 'count-missing-timestamps': // Backwards compatibility alias
      case 'missing-timestamps':
        return <MissingTimestamps activeFile={activeFile} />;
      case 'missing-activities':
        return <MissingActivities activeFile={activeFile} />;
      case 'missing-object-types':
        return <MissingObjectTypes activeFile={activeFile} />;
      case 'events-without-objects': // Backwards compatibility alias
      case 'missing-event-object-ref':
        return <MissingEventObjectRef activeFile={activeFile} />;
      case 'event-types-per-object-type':
        return <EventTypesPerObjectTypeTile {...props} />;
      case 'object-type-event-intensity': // Backwards compatibility alias
      case 'event-intensity-per-object-type':
        return <EventIntensityPerObjectTypeTile activeFile={activeFile} />;
      case 'event-counts-per-object-type':
        return <EventCountsPerObjectTypeTile activeFile={activeFile} />;
      case 'activity-counts': // Backwards compatibility alias
      case 'activity-counts-tile':
        return <ActivityCountsTile activeFile={activeFile} />;
      case 'object-type-combinations':
        return <ObjectTypeCombinationsTile activeFile={activeFile} />;
      case 'object-type-frequencies':
        return <ObjectTypeFrequenciesTile activeFile={activeFile} />;
      case 'orphan-objects':
        return <OrphanObjectsTile activeFile={activeFile} />;
      case 'activity-sequence-builder': // Backwards compatibility alias
      case 'pre-activity-selector':
        return <PreActivitySelector {...props} />;
      case 'process-graph': // Backwards compatibility alias
      case 'ocdfg-viewer':
        return (
          <Suspense fallback={<div className="p-4 text-center text-gray-500">Loading process graph…</div>}>
            <OCDFGViewer {...props} />
          </Suspense>
        );
      case 'object-details':
        return <ObjectDetails {...props} />;
      case 'cache-settings':
        return <CacheSettings {...props} />;
      case 'avg-events-per-object-type':
        return <AvgEventsPerObjectType activeFile={activeFile} />;
      // activity-frequency-chart removed in this branch
      case 'root-cause-analysis':
        return <RootCauseWizard {...props} />;
      default:
        return <div className="p-4 text-center text-gray-500">Unknown component: {component.id}</div>;
    }
  }, [activeFile, activityName, objectType, handleKennwortDataLoaded, handleTitleChange, restoredComponentSettings]);

  // True when the root-cause-analysis component is present on the current tab
  const isRcaActive = useMemo(() => {
    return allComponents.some(c => c.id === 'root-cause-analysis' && c.tab === activeTab);
  }, [allComponents, activeTab]);

  // Get components that should be visible based on active tab
  const getVisibleComponents = useCallback(() => {
    return allComponents.filter(comp =>
      comp.tab === activeTab || comp.tab === 'global'
    );
  }, [allComponents, activeTab]);

  // Get components for a specific tab (used for rendering all tabs while keeping them mounted)
  const getComponentsForTab = useCallback((tabId) => {
    return allComponents.filter(comp =>
      comp.tab === tabId || comp.tab === 'global'
    );
  }, [allComponents]);

  // State for persisting user layout modifications per tab
  const [persistedLayouts, setPersistedLayouts] = useState({});

  // Builds the dashboard config payload from current state.
  // active_file is included as save-time METADATA only (provenance + the
  // tenant signal for backend cache warming). applyConfig never reads it —
  // dataset selection stays per-browser/per-user and must never be restored
  // from a (potentially shared) config.
  const buildConfigPayload = useCallback(() => {
    const liveIds = new Set(allComponents.map((c) => c.instanceId || c.id));
    const currentSettings = {};
    Object.entries(componentSettingsGetters.current).forEach(([id, getter]) => {
      if (!liveIds.has(id)) return;
      try {
        currentSettings[id] = getter();
      } catch (e) {
        console.warn(`Settings getter failed for ${id}:`, e);
      }
    });
    // Merge: prefer live getter results; fall back to last-saved for tiles that
    // crashed or whose getter threw. Prune IDs no longer on the board.
    const mergedSettings = {};
    Object.entries({ ...restoredComponentSettings, ...currentSettings }).forEach(([id, s]) => {
      if (liveIds.has(id)) mergedSettings[id] = s;
    });
    try {
      const rfgSettings = reactFlowGraphSettingsGetter.current?.();
      if (rfgSettings) mergedSettings['react-flow-graph'] = rfgSettings;
    } catch (e) {
      console.warn('Settings getter failed for react-flow-graph:', e);
    }
    return {
      active_file: activeFile ?? null,
      tabs: tabs.map((t) => ({ id: t.id, label: t.label, showProcessGraph: t.showProcessGraph ?? false, tabFilters: t.tabFilters ?? null })),
      components: allComponents,
      layouts: persistedLayouts,
      component_settings: mergedSettings,
    };
  }, [activeFile, tabs, allComponents, persistedLayouts, restoredComponentSettings]);

  // Duplicates a whole tab: the tab itself, every component on it, their full
  // live settings, and their layout positions — all under fresh instance ids
  // so the copy shares no state with the original. Uses buildConfigPayload()
  // to get a live snapshot (it already merges each mounted tile's current
  // settings-getter result with last-saved settings, and works for inactive
  // tabs too since every tab stays mounted — see the "kept mounted" comment
  // near the tab-grid render below). State-only, like create/rename/remove —
  // persisted later via the existing Save button, not auto-saved.
  const handleDuplicateTab = useCallback((sourceTabId) => {
    if (workshopMode) return;
    const sourceTab = tabs.find((tb) => tb.id === sourceTabId);
    if (!sourceTab) return;

    const payload = buildConfigPayload();
    const newTabId = `custom-${Date.now()}`;
    const newLabel = t('sidebar.duplicateSuffix', { label: sourceTab.label });

    // Only components actually owned by this tab — 'global' components are
    // shared singletons and must not be cloned into the new tab.
    const sourceComponents = payload.components.filter((c) => c.tab === sourceTabId);

    // One shared timestamp + per-item index avoids instanceId collisions when
    // cloning several components in the same synchronous batch (plain
    // `Date.now()` per item can return the same millisecond for two items).
    const cloneStamp = Date.now();
    const idMap = new Map();
    const clonedComponents = sourceComponents.map((comp, index) => {
      if (!comp.instanceId) return { ...comp, tab: newTabId }; // static component — no instanceId to remap
      const oldId = comp.instanceId;
      const newInstanceId = `${comp.id}-${cloneStamp}${String(index).padStart(3, '0')}`;
      idMap.set(oldId, newInstanceId);
      return { ...comp, tab: newTabId, instanceId: newInstanceId };
    });

    const sourceLayouts = payload.layouts[sourceTabId];
    const clonedLayouts = sourceLayouts ? JSON.parse(JSON.stringify(sourceLayouts)) : undefined; // deep clone
    if (clonedLayouts) {
      Object.keys(clonedLayouts).forEach((breakpoint) => {
        clonedLayouts[breakpoint] = clonedLayouts[breakpoint].map((item) => ({
          ...item,
          i: idMap.get(item.i) ?? item.i,
        }));
      });
    }

    const clonedSettingsMap = {};
    sourceComponents.forEach((comp) => {
      const oldId = comp.instanceId || comp.id;
      const settings = payload.component_settings[oldId];
      if (!settings) return;
      const newId = idMap.get(oldId) ?? oldId;
      clonedSettingsMap[newId] = JSON.parse(JSON.stringify(settings)); // deep clone — breaks aliasing with the original
    });

    // Mirror applyConfig's exact title-precedence so the copy's tile headers
    // show the right custom title immediately, without requiring a reload.
    const clonedTitles = {};
    Object.entries(clonedSettingsMap).forEach(([newId, settings]) => {
      const title = settings?.componentTitle ?? settings?.kpiTitle ?? settings?.title;
      if (title) clonedTitles[newId] = title;
    });

    const newTab = createTab(newTabId, newLabel, {
      showProcessGraph: sourceTab.showProcessGraph ?? false,
      tabFilters: sourceTab.tabFilters ? JSON.parse(JSON.stringify(sourceTab.tabFilters)) : null,
    });

    // Incremental appends only — never applyConfig(), which wholesale-replaces
    // tabs/components and would wipe out every other tab.
    setTabs((prev) => [...prev, newTab]);
    setAllComponents((prev) => [...prev, ...clonedComponents]);
    if (clonedLayouts) {
      setPersistedLayouts((prev) => ({ ...prev, [newTabId]: clonedLayouts }));
    }
    if (Object.keys(clonedSettingsMap).length) {
      setRestoredComponentSettings((prev) => ({ ...prev, ...clonedSettingsMap }));
    }
    if (Object.keys(clonedTitles).length) {
      setComponentTitles((prev) => ({ ...prev, ...clonedTitles }));
    }
    setActiveTab(newTabId); // jump to the copy so the user sees it immediately
    // Deliberately no setAutoEditTabId here (unlike handleCreateTab) — the
    // label is already meaningfully filled in. Deliberately no configEpoch
    // bump either — that force-remounts every tile on every tab and is
    // reserved for full config load/import; the new instanceIds already
    // mount fresh naturally.
  }, [workshopMode, tabs, buildConfigPayload, t]);

  // Applies a config object to all dashboard state setters (used on load and import).
  // Filters config.components through whatever `allowedComponentIds` is known
  // at the time of the call (undefined/null both mean "don't filter" — see the
  // comment on the allowedComponentIds state declaration above). The
  // initial-load caller in fetchActiveFile runs this before the allowlist has
  // had a chance to resolve, so that first application doesn't itself strip a
  // disallowed tile out of `allComponents` state — the render-time filter
  // chains (which re-read the live, reactively-updated `allowedComponentIds`
  // on every render) are the actual enforcement point once the real value
  // arrives, same as the grid renderer never trusting stale state elsewhere.
  const applyConfig = useCallback((config) => {
    const restoredTabs = config.tabs?.map((t) => createTab(t.id, t.label, { showProcessGraph: t.showProcessGraph ?? false, tabFilters: t.tabFilters ?? null })) ?? [];
    // Safety net: a config must never restore to zero tabs, even though any
    // individual tab (including 'home') can now be removed and saved that way.
    if (restoredTabs.length === 0) {
      restoredTabs.push(createTab('home', 'Home'));
    }
    setTabs(restoredTabs);
    if (config.components?.length) {
      setAllComponents(filterComponentsByAllowlist(config.components, allowedComponentIds));
    }
    if (config.layouts) setPersistedLayouts(config.layouts);
    if (config.component_settings) {
      setRestoredComponentSettings(config.component_settings);
      // Restore custom tile header titles. Each component saves its title under
      // a different key (componentTitle / kpiTitle / title) depending on its template.
      const restoredTitles = {};
      Object.entries(config.component_settings).forEach(([id, settings]) => {
        const title = settings?.componentTitle ?? settings?.kpiTitle ?? settings?.title;
        if (title) restoredTitles[id] = title;
      });
      setComponentTitles(restoredTitles);
    }
    setConfigEpoch((n) => n + 1);
  }, [allowedComponentIds]);

  // Save the current dashboard config (per-browser; see useDashboardConfig).
  const handleSaveConfig = useCallback(async () => {
    const payload = buildConfigPayload();
    try {
      await saveConfig(payload);
      // Keep the in-memory snapshot in sync so tile remounts restore the last saved state.
      setRestoredComponentSettings(payload.component_settings);
      return true;
    } catch (e) {
      console.error('Failed to save dashboard config:', e?.response?.data?.detail ?? e?.message ?? e);
      return false;
    }
  }, [buildConfigPayload, saveConfig]);

  // Download the current config as a JSON file for sharing.
  const handleExportConfig = useCallback(() => {
    const payload = buildConfigPayload();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cintelion-dashboard-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [buildConfigPayload]);

  // Load a config from a JSON file and apply it to the dashboard.
  const handleImportConfig = useCallback(async (file) => {
    try {
      const text = await file.text();
      const config = JSON.parse(text);
      applyConfig(config);
      saveConfig(config).catch((e) => console.warn('Could not persist imported config:', e.message));
    } catch (e) {
      console.error('Failed to import config:', e);
    }
  }, [applyConfig, saveConfig]);

  // Shared config manager modal state
  const [configManagerOpen, setConfigManagerOpen] = useState(false);

  // Persists a config payload directly to localStorage + server without going
  // through buildConfigPayload (which reads stale React state during the same
  // render cycle as applyConfig). Used by the Config Manager to save a loaded
  // shared config as the user's personal startup config.
  const handlePersistConfig = useCallback(async (config) => {
    try {
      await saveConfig(config);
    } catch (e) {
      console.error('Failed to persist config:', e?.message ?? e);
    }
  }, [saveConfig]);

  // Generate unified grid layout including dynamic components
  const generateUnifiedLayout = useCallback((forTab = activeTab) => {
    // Get visible components for specified tab
    const visibleComponents = getComponentsForTab(forTab);
    const visibleComponentIds = visibleComponents.map(comp => comp.instanceId || comp.id);


    // Get tab-specific persisted layouts
    const tabPersistedLayouts = persistedLayouts[forTab];

    // Start with base layouts, filtered for visible components only
    const layouts = JSON.parse(JSON.stringify(UNIFIED_GRID_LAYOUTS)); // Deep clone

    // Filter base layouts to only include visible components (only static components at this stage)
    Object.keys(layouts).forEach(breakpoint => {
      layouts[breakpoint] = layouts[breakpoint].filter(item =>
        visibleComponentIds.includes(item.i)
      );
    });

    // Apply persisted layouts if they exist for this tab (includes persisted dynamic components)
    if (tabPersistedLayouts) {
      Object.keys(layouts).forEach(breakpoint => {
        const persistedBreakpoint = tabPersistedLayouts[breakpoint];
        if (persistedBreakpoint) {
          // Build constraint map from base layouts before overwriting
          const baseConstraints = new Map(
            (layouts[breakpoint] || []).map(item => [item.i, item])
          );

          // Start with persisted layouts filtered for visible components,
          // but re-apply minH/maxH/minW/maxW from base layouts so constraint
          // changes in UNIFIED_GRID_LAYOUTS take effect after a page reload.
          const visiblePersistedItems = persistedBreakpoint
            .filter(item => visibleComponentIds.includes(item.i))
            .map(item => {
              const base = baseConstraints.get(item.i);
              if (!base) {
                const baseId = item.i.replace(/-\d+$/, '');
                const def = DEFAULT_COMPONENT_LAYOUTS[baseId];
                if (def?.minW != null) return { ...item, minW: def.minW };
                return item;
              }
              return {
                ...item,
                ...(base.minH != null && { minH: base.minH }),
                ...(base.maxH != null && { maxH: base.maxH }),
                ...(base.minW != null && { minW: base.minW }),
                ...(base.maxW != null && { maxW: base.maxW }),
              };
            });

          layouts[breakpoint] = visiblePersistedItems;
        }
      });
    }

    // Add new dynamic components that don't have layout positions yet
    const dynamicComponents = visibleComponents.filter(comp => comp.type === 'dynamic');
    const componentsNeedingLayout = dynamicComponents.filter(component => {
      const componentId = component.instanceId || component.id;
      return !layouts.lg.some(item => item.i === componentId);
    });

    if (componentsNeedingLayout.length > 0) {
      const maxY = layouts.lg.length > 0 ? Math.max(...layouts.lg.map(item => item.y + item.h)) : 0;

      componentsNeedingLayout.forEach((component, index) => {
        const componentId = component.instanceId || component.id;
        const baseComponentId = component.id; // Base component ID without timestamp
        const baseY = maxY + Math.floor(index / 3) * 6; // Stack below existing components with proper spacing
        const x = (index % 3) * 4; // 3 columns

        // Check if there's a predefined layout for this component type in UNIFIED_GRID_LAYOUTS.
        // Also check reverse alias (e.g. 'lead-time-statistics' → 'attr-stats').
        const reverseAliasId = Object.keys(COMPONENT_ID_ALIASES).find(
          old => COMPONENT_ID_ALIASES[old] === baseComponentId
        );
        const predefinedLayout =
          UNIFIED_GRID_LAYOUTS.lg.find(item => item.i === baseComponentId) ||
          (reverseAliasId && UNIFIED_GRID_LAYOUTS.lg.find(item => item.i === reverseAliasId));

        // Use predefined sizes if available, otherwise use defaults
        let lgWidth = 6, lgHeight = 6;
        let mdWidth = 5, mdHeight = 6;
        let smWidth = 6, smHeight = 6;
        let xsWidth = 4, xsHeight = 6;
        let xxsWidth = 2, xxsHeight = 6;

        if (predefinedLayout) {
          // Use the predefined dimensions from UNIFIED_GRID_LAYOUTS
          lgWidth = predefinedLayout.w;
          lgHeight = predefinedLayout.h;

          // Find corresponding layouts for other breakpoints (also check reverse alias)
          const findBp = (bp, id) =>
            UNIFIED_GRID_LAYOUTS[bp].find(item => item.i === id) ||
            (reverseAliasId && UNIFIED_GRID_LAYOUTS[bp].find(item => item.i === reverseAliasId));
          const mdLayout = findBp('md', baseComponentId);
          const smLayout = findBp('sm', baseComponentId);
          const xsLayout = findBp('xs', baseComponentId);
          const xxsLayout = findBp('xxs', baseComponentId);

          if (mdLayout) { mdWidth = mdLayout.w; mdHeight = mdLayout.h; }
          if (smLayout) { smWidth = smLayout.w; smHeight = smLayout.h; }
          if (xsLayout) { xsWidth = xsLayout.w; xsHeight = xsLayout.h; }
          if (xxsLayout) { xxsWidth = xxsLayout.w; xxsHeight = xxsLayout.h; }
        }

        const lgMinH = predefinedLayout?.minH ?? DEFAULT_COMPONENT_LAYOUTS[baseComponentId]?.minH ?? 2;
        const lgMinW = predefinedLayout?.minW ?? DEFAULT_COMPONENT_LAYOUTS[baseComponentId]?.minW ?? 3;
        const lgMaxH = predefinedLayout?.maxH;
        layouts.lg.push({ i: componentId, x: 0, y: baseY, w: lgWidth, h: lgHeight, minW: lgMinW, minH: lgMinH, ...(lgMaxH && { maxH: lgMaxH }) });
        layouts.md.push({ i: componentId, x: 0, y: maxY + index * mdHeight, w: mdWidth, h: mdHeight, minW: lgMinW, minH: lgMinH, ...(lgMaxH && { maxH: lgMaxH }) });
        layouts.sm.push({ i: componentId, x: 0, y: maxY + index * smHeight, w: smWidth, h: smHeight, minW: lgMinW, minH: lgMinH, ...(lgMaxH && { maxH: lgMaxH }) });
        layouts.xs.push({ i: componentId, x: 0, y: maxY + index * xsHeight, w: xsWidth, h: xsHeight, minW: Math.min(lgMinW, 2), minH: lgMinH, ...(lgMaxH && { maxH: lgMaxH }) });
        layouts.xxs.push({ i: componentId, x: 0, y: maxY + index * xxsHeight, w: xxsWidth, h: xxsHeight, minW: 2, minH: lgMinH, ...(lgMaxH && { maxH: lgMaxH }) });
      });
    }



    return layouts;
  }, [allComponents, persistedLayouts, getComponentsForTab]);

  // Generate layouts for all tabs to keep them mounted
  const allTabLayouts = useMemo(() => {
    const layouts = {};
    tabs.forEach(tab => {
      layouts[tab.id] = generateUnifiedLayout(tab.id);
    });
    return layouts;
  }, [tabs, generateUnifiedLayout]);

  const currentLayouts = useMemo(
    () => allTabLayouts[activeTab] ?? {},
    [allTabLayouts, activeTab]
  );

  // Resolve the per-browser active file and load saved dashboard config.
  // The browser's own selection (localStorage) wins; the server-side active
  // file is only a default for first-time visitors so one user's dataset
  // switch never affects another user's session.
  useEffect(() => {
    const fetchActiveFile = async () => {
      try {
        const files = await getAvailableFiles();
        const availableTypes = new Set((files || []).map((f) => f.file_type));

        let candidate = getStoredActiveFile();
        if (candidate && !availableTypes.has(candidate)) {
          candidate = null;
        }

        // For a real logged-in account (never the shared "default" identity
        // used when auth is disabled, which would otherwise let one opted-in
        // "user" affect every anonymous browser), the account's own
        // Supabase-backed dashboard state is authoritative — it's what makes
        // "last active dataset" and "always use latest scheduled data" follow
        // the user across browsers/devices instead of being stuck per-browser.
        let latestModeActive = false;
        let lastSelectionAt = null;
        // Hoisted (not block-scoped to the `if` below) so the dashboard-config
        // sync step further down — which also needs this same fetched row's
        // dashboard_config_saved_at — can reuse it without a second round trip.
        let dashboardState = null;
        if (user?.id) {
          dashboardState = await getUserDashboardState().catch(() => null);
          if (dashboardState?.last_active_file_type && availableTypes.has(dashboardState.last_active_file_type)) {
            candidate = dashboardState.last_active_file_type;
          }
          if (dashboardState?.auto_activate_latest_etl_file) {
            latestModeActive = true;
          }
          lastSelectionAt = dashboardState?.last_active_file_set_at ?? null;
        }

        // Skip the URL's ?file= when latest-mode is active: the app
        // continuously rewrites ?file= to match whatever is currently active
        // (see the effect below), so a reload's URL always carries the file
        // that was active *before* this refresh — it would otherwise mask
        // the server's up-to-date default just like localStorage would.
        if (!candidate && !latestModeActive) {
          const fileFromUrl = searchParams.get('file');
          if (fileFromUrl) {
            const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            const matched = (files || []).find(
              f => slugify(f.display_name) === fileFromUrl || f.file_type === fileFromUrl
            );
            if (matched) candidate = matched.file_type;
          }
        }

        if (latestModeActive) {
          // Scope the "latest scheduled" lookup to the candidate's own ETL
          // customer, so an opted-in user is never jumped onto a different
          // customer's dataset just because that customer's scheduled run
          // happened to land first — see GET /ocel/active-file. Files without
          // a customer (system datasets, manual uploads) have no "latest
          // scheduled" notion, so latest-mode skips them entirely: comparing
          // against the global first-visit default instead would let any
          // colleague's "Open Project" click yank opted-in users around.
          const candidateCustomerId = candidate
            ? (files || []).find(f => f.file_type === candidate)?.customer_id
            : null;
          if (candidateCustomerId) {
            try {
              const response = await getActiveFileType(candidateCustomerId);
              const serverLatest = response.active_file_type;
              // Jump only onto an activation NEWER than last_active_file_set_at,
              // which setStoredActiveFile bumps to "now" on every mount (not just
              // deliberate Open Project / File Manager switches) — so this is really
              // "newer than the last time this user's session confirmed the active
              // file," not a literal explicit-selection timestamp. That's still
              // correct: a deliberate switch back to an older file of the same
              // customer survives reloads until the next scheduled run lands, because
              // each mount re-queries the server's current activation fresh.
              // No activated_at (global fallback or legacy record) → no jump.
              const activatedAt = response.activated_at ? Date.parse(response.activated_at) : NaN;
              const selectedAt = lastSelectionAt ? Date.parse(lastSelectionAt) : NaN;
              const isNewerActivation =
                !Number.isNaN(activatedAt) && (Number.isNaN(selectedAt) || activatedAt > selectedAt);
              if (serverLatest && availableTypes.has(serverLatest) && serverLatest !== candidate && isNewerActivation) {
                candidate = serverLatest;
                setStoredActiveFile(candidate); // persists + syncs the jump so other devices converge too
              }
            } catch (_) {
              // Server lookup unavailable — fall through to whatever candidate we already have
            }
          }
        }

        if (!candidate) {
          try {
            const response = await getActiveFileType();
            const serverDefault = response.active_file_type;
            if (serverDefault && availableTypes.has(serverDefault)) {
              candidate = serverDefault;
            }
          } catch (_) {
            // Server default unavailable — fall through to /projects
          }
        }

        if (!candidate) {
          if (canAccessProjects) {
            navigate('/projects');
            return;
          }
          // No dataset resolved and this user can't get into /projects to pick
          // one — navigating there would just bounce them straight back here
          // (ProtectedRoute redirects non-opted-in users to /), producing a
          // silent infinite loop. Show a clear message instead.
          setInitError('No dataset is available, and you do not have access to project management. Please contact your administrator.');
          return;
        }
        setStoredActiveFile(candidate);
        setActiveFile(candidate);

        // Deliberately no allowlist pre-fetch here (a prior fix round tried
        // this and reverted it — see the comment on the allowedComponentIds
        // state declaration above): it duplicated OCELFileManager's own
        // GET /ocel/metadata (un-coalesced, since that call already resolves
        // and clears its dedup entry before this one would fire) and, worse,
        // put a new network round-trip on the isInitializing critical path.
        // The allowlist instead resolves reactively via
        // AllowedComponentIdsResolver, rendered inside <AppProviders>, once
        // OCELFileManager's existing mount-time metadata fetch lands.

        // Load saved dashboard config after file is known. For a logged-in
        // user, pull a newer config saved from another device — reuses the
        // same dashboardState fetched above for the active-file logic, no
        // extra round trip. Never applies config.active_file (see applyConfig).
        let config = await loadConfig();
        if (user?.id) {
          config = await syncFromServerIfNewer(config, dashboardState?.dashboard_config_saved_at);
          // Adopt the "active shared config" marker too, so the Config
          // Manager's green "Aktiv" badge matches across devices — this is
          // just the id/name pointer (activeSharedConfigStorage.js); the
          // config's own content already synced above.
          if (dashboardState?.active_shared_config_id) {
            setStoredActiveSharedConfigId(user.id, dashboardState.active_shared_config_id);
          }
        }
        if (config) {
          applyConfig(config);
        }
      } catch (error) {
        console.error('Error fetching active file:', error);
        if (canAccessProjects) {
          navigate('/projects');
        } else {
          // Same bounce-loop concern as above: a user without /projects access
          // would just be redirected right back here.
          setInitError('No dataset is available, and you do not have access to project management. Please contact your administrator.');
        }
      } finally {
        setIsInitializing(false);
      }
    };
    fetchActiveFile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to determine if controls should be shown for a component
  const shouldShowControls = (component) => {
    if (workshopMode) return { showClose: false, showRefresh: true, showSettings: false };

    // Only hide controls for static (pre-configured) tiles
    if (component.type !== 'static') {
      return {
        showClose: true,
        showRefresh: true,
        showSettings: true
      };
    }

    // For static tiles, check specific component IDs
    const componentId = component.id;

    // AttributeFilteredStats & LeadTimeHistogram: Hide only X button
    if (componentId === 'attr-stats' || componentId === 'histogram') {
      return {
        showClose: false,      // Hide X
        showRefresh: true,
        showSettings: true
      };
    }

    // All DateDeviationKPI tiles: Hide X, Settings, and Refresh
    if (componentId.startsWith('date-deviation-kpi')) {
      return {
        showClose: false,      // Hide X
        showRefresh: false,    // Hide Refresh
        showSettings: false    // Hide Settings
      };
    }

    // LiefertermintreueKPI: manages own settings button
    if (componentId === 'liefertermintreue-kpi') {
      return {
        showClose: true,
        showRefresh: false,
        showSettings: false,
      };
    }

    // Default: show all controls for other static tiles
    return {
      showClose: true,
      showRefresh: true,
      showSettings: true
    };
  };

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading application...
      </div>
    );
  }

  // Blocking message in place of the dashboard — see the fetchActiveFile
  // effect above for why this can't just navigate('/projects') instead.
  if (initError) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666',
        textAlign: 'center',
        padding: '0 24px'
      }}>
        {initError}
      </div>
    );
  }

  return (
    <AppProviders>
      <FileSwitchFilterClearer activeFile={activeFile} />
      <AllowedComponentIdsResolver activeFile={activeFile} onResolve={setAllowedComponentIds} />
      <FileSwitchLoadingOverlay />
      <div className="relative min-h-screen w-full" style={{ background: 'linear-gradient(to bottom, rgba(244,247,249,0) 71px, rgba(244,247,249,0.3) 130px, rgba(244,247,249,0.65) 200px, rgba(244,247,249,1) 600px), linear-gradient(to right, #2C65E8, #41CCAB)' }}>
            {/* Header - Full Width Gradient */}
            <AppHeader />

            {/* Sidebar - Fixed Overlay */}
            <div className="fixed left-0 top-0 z-40 h-full">
              <AppSidebar
                activeTab={activeTab}
                onTabChange={setActiveTab}
                tabs={tabs}
                activeFile={activeFile}
                onCreateTab={handleCreateTab}
                onRemoveTab={handleRemoveTab}
                onRenameTab={handleRenameTab}
                onToggleProcessGraph={handleToggleProcessGraph}
                onUpdateTabFilters={handleUpdateTabFilters}
                onDuplicateTab={handleDuplicateTab}
                onReorderTabs={handleReorderTabs}
                startEditingTabId={autoEditTabId}
              />
            </div>

            {/* Main Content - Full Width */}
            <div className="flex flex-col min-h-screen w-full">
              <main className="flex-1 min-h-0 p-6 w-full max-w-none pt-16">

                {/* Render all tab grids (kept mounted to preserve state) */}
                {tabs
                  .map(tab => {
                    const isActiveTab = activeTab === tab.id;
                    const tabLayouts = allTabLayouts[tab.id] || currentLayouts;
                    const tabComponents = getComponentsForTab(tab.id);

                    return (
                      <TabFilterProvider key={tab.id} tabFilters={tab.tabFilters}>
                      <ContentSection
                        spacing="sm"
                        className="w-full max-w-none"
                        style={{ display: isActiveTab ? 'block' : 'none' }}
                      >
                        {tab.id === 'logCheck' && !activeFile ? (
                          <div className="p-6 text-center text-gray-600 bg-white border border-gray-200 rounded-lg">
                            Select an OCEL/Event Log in the File Manager to view data quality KPIs.
                          </div>
                        ) : (
                          <GridLayout
                            layoutKey={`unified-${tab.id}`} // Use unique layout key per tab
                            isActive={isActiveTab}
                            defaultLayouts={tabLayouts}
                            isDraggable={!workshopMode}
                            isResizable={!workshopMode}
                            onLayoutChange={(layout, allLayouts) => {
                              // Persist user layout changes per tab
                              setPersistedLayouts(prev => ({
                                ...prev,
                                [tab.id]: allLayouts
                              }));
                            }}
                            gridProps={{ showControls: true }}
                            className="w-full"
                          >
                            {tabComponents
                              .filter(component => !NON_GRID_COMPONENTS.includes(component.id)
                                && (allowedComponentIds == null || allowedComponentIds.has(COMPONENT_ID_ALIASES[component.id] || component.id)))
                              .map((component) => {
                              const componentId = component.instanceId || component.id;

                              // Get the layout for this component from the current generated layouts
                              const componentLayout =
                                tabLayouts.lg?.find(item => item.i === componentId) ||
                                (DEFAULT_COMPONENT_LAYOUTS[component.id]
                                  ? { x: 0, y: 0, ...DEFAULT_COMPONENT_LAYOUTS[component.id] }
                                  : null) ||
                                { x: 0, y: 0, w: 6, h: 6, minW: 3, minH: 2 };

                              const refreshFunction = componentRefreshFunctions.current[componentId];
                              const headerActions = componentHeaderActions[componentId];

                              // Determine which controls to show
                              const controlVisibility = shouldShowControls(component);

                              return (
                                <div key={componentId} data-grid={componentLayout}>
                                  <GridComponent
                                    title={componentTitles[componentId] || component.name}
                                    headerVariant="minimal"
                                    // Conditionally pass onClose based on visibility
                                    onClose={controlVisibility.showClose ? () => handleComponentRemove(component) : undefined}
                                    headerActions={
                                      <>
                                        {/* Filter out settings button if needed */}
                                        {headerActions && controlVisibility.showSettings && headerActions}

                                        {/* Conditionally render refresh button */}
                                        {refreshFunction && controlVisibility.showRefresh && (
                                          <RefreshButton
                                            onClick={() => componentRefreshFunctions.current[componentId]?.()}
                                            size="sm"
                                            title={`Refresh ${component.name}`}
                                          />
                                        )}
                                      </>
                                    }
                                  >
                                    <div key={`${activeFile}:${configEpoch}`} className="h-full grid-component-wrapper" style={{ pointerEvents: 'auto' }}>
                                      <LazyTileMount id={componentId} tab={tab.id} isActiveTab={isActiveTab} gridY={componentLayout.y}>
                                        {renderComponent(component)}
                                      </LazyTileMount>
                                    </div>
                                  </GridComponent>
                                </div>
                              );
                            })}
                          </GridLayout>
                        )}
                        {/* Non-grid components rendered below the grid for this tab */}
                        {tabComponents
                          .filter(c => NON_GRID_COMPONENTS.includes(c.id)
                            && (allowedComponentIds == null || allowedComponentIds.has(COMPONENT_ID_ALIASES[c.id] || c.id)))
                          .map(component => {
                            const componentId = component.instanceId || component.id;
                            return (
                              <div key={componentId} className="mt-4">
                                <GridComponent
                                  title={component.name}
                                  headerVariant="minimal"
                                  onClose={() => handleComponentRemove(component)}
                                >
                                  <div key={`${activeFile}:${configEpoch}`} className="h-full">
                                    {renderComponent(component)}
                                  </div>
                                </GridComponent>
                              </div>
                            );
                          })}
                      </ContentSection>
                      </TabFilterProvider>
                    );
                  })
                }

                {/* Toolbar Buttons */}
                <ToolbarButtons
                  activeFile={activeFile}
                  onFileChange={handleFileChange}
                  onDisplayNameChange={handleDisplayNameChange}
                  onComponentAdd={handleComponentAdd}
                  onOpenConfigManager={() => setConfigManagerOpen(true)}
                  allowedComponentIds={allowedComponentIds}
                />

                {/* Config Manager — Save / Import file / Export file / Shared configs */}
                <ConfigManagerModal
                  open={configManagerOpen}
                  onClose={() => setConfigManagerOpen(false)}
                  currentUserId={user?.id ?? 'default'}
                  onBuildPayload={buildConfigPayload}
                  onApplyConfig={applyConfig}
                  onPersistConfig={handlePersistConfig}
                  onSave={handleSaveConfig}
                  onExport={handleExportConfig}
                  onImport={handleImportConfig}
                  isSaving={isSaving}
                  saveError={saveError}
                />

                {/* Process Graph Section — only on tabs where the user enabled it */}
                {tabs.find(t => t.id === activeTab)?.showProcessGraph && (
                  <ContentSection spacing="lg" className="w-full max-w-none">
                    <div className="w-full">
                      <Suspense fallback={<div className="p-4 text-center text-gray-500">Loading React Flow Viewer…</div>}>
                        <ReactFlowViewer
                          activeFile={activeFile}
                          activeTab={activeTab}
                          isRcaActive={isRcaActive}
                          initialSettings={restoredComponentSettings?.['react-flow-graph'] ?? null}
                          onRegisterSettingsGetter={(getter) => { reactFlowGraphSettingsGetter.current = getter; }}
                        />
                      </Suspense>
                    </div>
                  </ContentSection>
                )}
              </main>
            </div>
      </div>

      {/* Floating "Switch Project" button — lower right corner. Hidden for
          users without /projects access — ProtectedRoute would just bounce
          them straight back here, so showing it would only be a dead end. */}
      <HideInWorkshop>
        {canAccessProjects && (
        <button
          onClick={() => navigate('/projects')}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-3.5 py-2.5 bg-white border border-gray-200 shadow-lg rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:shadow-xl transition-all"
          title="Switch project"
        >
          <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5 7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
          </svg>
          <span className="max-w-[140px] truncate font-mono text-xs text-gray-500">{activeFile ?? 'No project'}</span>
        </button>
        )}
      </HideInWorkshop>

    </AppProviders>
  );
}

export default App;
